import React from 'react';
import ManageServiceProviderFileGroupingComponentVM from './ManageServiceProviderFileGroupingComponentVM';
import { observer } from 'mobx-react';
import { Neo, NeoGrid } from '@singularsystems/neo-react';
import { TextConstants } from '../../../Common/TextConstants';
import { EnumHelper, List, ModalUtils, Validation, Misc } from '@singularsystems/neo-core';
import TradeSftpTypeLookup from '../Models/Lookups/TradeSftpTypeLookup';
import { TradeDistributionTypeEnum } from '../Models/Enums/TradeDistributionTypeEnum';
import { ExportEventTypeEnum } from '../Models/Enums/ExportEventTypeEnum';
import ServiceProviderFileGroupingExportSettingLookup from '../Models/Lookups/ServiceProviderFileGroupingExportSettingLookup';

interface IManageServiceProviderFileGroupingComponentProps {
    viewModel: ManageServiceProviderFileGroupingComponentVM;
    tradeSftpTypeOptions: List<TradeSftpTypeLookup>;
    onSave: () => void | Promise<void>;
    onCancel: () => void;
}

@observer
export default class ManageServiceProviderFileGroupingComponent extends React.Component<IManageServiceProviderFileGroupingComponentProps> {
    private handlePageCancel = async () => {
        const viewModel = this.props.viewModel;
        if (viewModel.hasUnsavedChanges) {
            const result = await ModalUtils.showYesNo(
                'Unsaved Settings Changes',
                'You have unsaved changes. Are you sure you want to exit?'
            );
            if (result === Misc.ModalResult.Yes) {
                viewModel.confirmDiscardChanges();
                this.props.onCancel();
            }
        } else {
            this.props.onCancel();
        }
    }

    private renderCardTitleWithUnsavedBadge(title: string, hasUnsavedChanges: boolean): React.ReactNode {
        return (
            <div className="d-flex align-items-center w-100">
                <span>{title}{hasUnsavedChanges && '\u00A0\u00A0\u00A0'}</span>
                {hasUnsavedChanges && (
                    <span
                        className="badge rounded-pill bg-warning text-dark d-inline-flex align-items-center"
                        title={`You have unsaved changes in ${title}. Click Save to save changes.`}
                        style={{ minHeight: '20px', padding: '2px 8px', whiteSpace: 'nowrap' }}>
                        <i className="fa fa-exclamation-triangle me-1" aria-hidden="true" />
                        <small><strong>Unsaved Changes</strong></small>
                    </span>
                )}
            </div>
        );
    }

    public render() {
        const viewModel = this.props.viewModel;

        const lastLBVHBadge: React.ReactNode = viewModel.hasLastLBVH ? (() => {
            const textClass = viewModel.lastLBVHVariant === 'secondary'
                ? 'text-muted'
                : viewModel.lastLBVHVariant === 'warning' ? 'text-dark' : 'text-white';

            const textStyle: React.CSSProperties | undefined = viewModel.lastLBVHVariant === 'danger'
                ? { color: '#ffffff' }
                : undefined;

            return (
                <span
                    className={`badge rounded-pill bg-${viewModel.lastLBVHVariant} me-2 ${textClass} px-3 d-inline-flex align-items-center`}
                    title={viewModel.lastLBVHDisplay}
                    style={{ minHeight: '34px', ...textStyle }}>
                    <i className="fa fa-clock-o me-1" aria-hidden="true" />
                    <strong style={textStyle}>Last LBVH:</strong>
                    {viewModel.lastLBVHRelative}
                </span>
            );
        })() : null;

        return (
            <React.Fragment>
                <Neo.Form
                    model={viewModel.managedServiceProviderFileGrouping}
                    onSubmit={async () => {
                        viewModel.confirmDiscardChanges();
                        await Promise.resolve(this.props.onSave());
                    }}
                    validationDisplayMode={Validation.DisplayMode.AfterSubmit}
                    onInvalid={this.onFormInvalid}>
                    <Neo.Card title={viewModel.addNewServiceProviderInd ? TextConstants.Titles.AddServiceProviderFileGrouping : TextConstants.Titles.EditServiceProviderFileGrouping}>
                        {!viewModel.addNewServiceProviderInd && viewModel.managedServiceProviderFileGrouping.isActiveInd && viewModel.managedServiceProviderFileGrouping.staticServiceProviderFileGroupingId > 0 &&
                            <div className="d-flex justify-content-end align-items-center pb-2 gap-2">
                                {lastLBVHBadge}
                                <Neo.Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => viewModel.confirmExecuteLBVH()}>
                                    {TextConstants.Buttons.ExecuteLBVH}
                                </Neo.Button>
                            </div>
                        }
                        <Neo.Card title={this.renderCardTitleWithUnsavedBadge("Service Provider Settings", viewModel.managedServiceProviderFileGrouping.isDirty)} className="mb-3">
                            {viewModel.addNewServiceProviderInd &&
                                <Neo.GridLayout lg={3}>
                                    <Neo.FormGroup bindContext={viewModel.managedServiceProviderFileGrouping.meta.serviceProviderName}>
                                        <Neo.AutoCompleteDropDown
                                            label={TextConstants.Labels.ServiceProvider}
                                            bind={viewModel.managedServiceProviderFileGrouping.meta.staticServiceProviderId}
                                            bindDisplay={viewModel.managedServiceProviderFileGrouping.meta.serviceProviderName}
                                            items={viewModel.serviceProviderList.sortBy((x) => x.staticServiceProviderId, "asc")}
                                            onItemSelected={() => viewModel.setSelectedServiceProvider(viewModel.managedServiceProviderFileGrouping.meta.staticServiceProviderId.value as number)}
                                        />
                                    </Neo.FormGroup>
                                </Neo.GridLayout>
                            }
                            <Neo.GridLayout lg={3}>
                                <Neo.FormGroup
                                    bind={viewModel.managedServiceProviderFileGrouping.meta.serviceProviderName}
                                    isReadOnly={viewModel.isReadOnlyInd} />
                                <Neo.FormGroup
                                    bind={viewModel.managedServiceProviderFileGrouping.meta.serviceProviderCode}
                                    isReadOnly={viewModel.isReadOnlyInd} />
                                <Neo.FormGroup
                                    bind={viewModel.managedServiceProviderFileGrouping.meta.serviceProviderSubAccountName}
                                    isReadOnly={viewModel.isReadOnlyInd} />
                            </Neo.GridLayout>
                            <Neo.GridLayout lg={3}>
                                <Neo.FormGroup
                                    bind={viewModel.managedServiceProviderFileGrouping.meta.serverName}
                                    isReadOnly={viewModel.isReadOnlyInd} />
                                <Neo.FormGroup label={TextConstants.Labels.DatabaseName} bind={viewModel.managedServiceProviderFileGrouping.meta.databaseName} />
                            </Neo.GridLayout>
                        </Neo.Card>

                        <Neo.Card title={this.renderCardTitleWithUnsavedBadge("File Grouping Settings", viewModel.managedServiceProviderFileGrouping.isDirty)} className="mb-3">
                            <Neo.GridLayout lg={3}>
                                <Neo.FormGroup label={TextConstants.Labels.Broker} bind={viewModel.managedServiceProviderFileGrouping.meta.serviceProviderGroupingName} />
                            </Neo.GridLayout>
                            <Neo.GridLayout lg={1}>
                                <Neo.FormGroup
                                    label={TextConstants.Labels.BrokerDescription}
                                    bind={viewModel.managedServiceProviderFileGrouping.meta.groupingDescription}
                                    input={{ rows: 3 }} />
                            </Neo.GridLayout>
                            <Neo.GridLayout lg={3}>
                                <Neo.FormGroupInline label={TextConstants.Labels.Onshore} bind={viewModel.managedServiceProviderFileGrouping.meta.isOnshoreInd} />
                                <Neo.FormGroupInline label={TextConstants.Labels.IgnoreGrouping} bind={viewModel.managedServiceProviderFileGrouping.meta.ignoreGroupingInd} />
                                <Neo.FormGroupInline label={TextConstants.Labels.IsActive} bind={viewModel.managedServiceProviderFileGrouping.meta.isActiveInd} />
                            </Neo.GridLayout>
                            <Neo.GridLayout lg={3}>
                                <Neo.FormGroupInline label={TextConstants.Labels.ImportsOnPublicHolidays} bind={viewModel.managedServiceProviderFileGrouping.meta.importsPublicHolidaysInd} />
                                <Neo.FormGroupInline label={TextConstants.Labels.IgnoreExchangeRates} bind={viewModel.managedServiceProviderFileGrouping.meta.ignoresExchangeRatesInd} />
                                <Neo.FormGroupInline label={TextConstants.Labels.ClientAlternativeRefSearch} bind={viewModel.managedServiceProviderFileGrouping.meta.clientAltSearchInd} />
                            </Neo.GridLayout>
                            <Neo.GridLayout lg={3}>
                                <Neo.FormGroupInline label={TextConstants.Labels.DividendGrossRepresentation} bind={viewModel.managedServiceProviderFileGrouping.meta.provideDividendGrossInd} />
                                <Neo.FormGroupInline label={TextConstants.Labels.UsesAccountCurrency} bind={viewModel.managedServiceProviderFileGrouping.meta.useAccountCurrencyInd} />
                                <Neo.FormGroupInline label={TextConstants.Labels.SortOrder} bind={viewModel.managedServiceProviderFileGrouping.meta.sortOrder} />
                            </Neo.GridLayout>
                            <Neo.GridLayout lg={3}>
                                <Neo.FormGroupInline label={TextConstants.Labels.IsWacEnabled} bind={viewModel.managedServiceProviderFileGrouping.meta.isWacEnabled} />
                                <Neo.FormGroupInline label={TextConstants.Labels.EventIdFieldName} bind={viewModel.managedServiceProviderFileGrouping.meta.eventIdFieldName} />
                                <Neo.FormGroupInline label={TextConstants.Labels.SAMReporting} bind={viewModel.managedServiceProviderFileGrouping.meta.samReporting} />
                            </Neo.GridLayout>
                            <Neo.GridLayout lg={3}>
                                <Neo.FormGroupInline label={TextConstants.Labels.IsBroker} bind={viewModel.managedServiceProviderFileGrouping.meta.isBroker} />
                                <Neo.FormGroupInline label={TextConstants.Labels.IsBookCostRunning} bind={viewModel.managedServiceProviderFileGrouping.meta.isBookCostRunning} isReadOnly />
                                <Neo.FormGroupInline
                                    label={TextConstants.Labels.LastDailyReportSync}
                                    bind={viewModel.managedServiceProviderFileGrouping.meta.lastDailyReportSync}
                                    isReadOnly
                                    append={
                                        <Neo.Button
                                            onClick={() => viewModel.dailySyncForGrouping(viewModel.managedServiceProviderFileGrouping)}
                                            variant="primary"
                                            isOutline
                                            size="sm"
                                            tooltip={TextConstants.Tooltips.DailySyncServiceProviderFileGrouping}
                                            aria-label={TextConstants.Tooltips.DailySyncServiceProviderFileGrouping}>
                                            <i className="fa fa-sync" />
                                        </Neo.Button>
                                    } />
                            </Neo.GridLayout>
                            <Neo.GridLayout lg={3}>
                                <Neo.FormGroupInline label={TextConstants.Labels.ExportToSFTP} bind={viewModel.managedServiceProviderFileGrouping.meta.exportToSFTP} input={{ type: "switch" }} />
                                <Neo.FormGroupInline label={TextConstants.Labels.ExportToAPI} bind={viewModel.managedServiceProviderFileGrouping.meta.exportToAPI} input={{ type: "switch" }} />
                            </Neo.GridLayout>
                            <Neo.GridLayout lg={3}>
                                <Neo.FormGroup label="Trade Distribution Types">
                                    <Neo.DropDown
                                        select={{
                                            items: EnumHelper.asList(TradeDistributionTypeEnum),
                                            allowNulls: true,
                                        }}
                                        bind={viewModel.managedServiceProviderFileGrouping.meta.tradeDistributionTypeId}
                                        placeholder="Select distribution type"
                                    />
                                </Neo.FormGroup>
                                <Neo.FormGroup
                                    label={TextConstants.Labels.CriticalFailureRecipients}
                                    footerText="Use , to separate email addresses"
                                    bind={viewModel.managedServiceProviderFileGrouping.meta.criticalFailureRecipients}
                                    input={{ rows: 2 }}
                                />
                            </Neo.GridLayout>
                        </Neo.Card>

                        {!viewModel.addNewServiceProviderInd &&
                            <Neo.Card title={this.renderCardTitleWithUnsavedBadge("Export Settings", viewModel.hasUnsavedExportSettingsChanges)} className="mb-3">
                                {!viewModel.isEditingExportSettingInd &&
                                    <div className="d-flex justify-content-end mb-2">
                                        <Neo.Button
                                            onClick={() => viewModel.addNewExportSetting()}
                                            variant="primary"
                                            size="sm">
                                            {TextConstants.Buttons.Add}
                                        </Neo.Button>
                                    </div>
                                }

                                <Neo.Modal
                                    bind={viewModel.meta.isEditingExportSettingInd}
                                    bindModel={viewModel.meta.currentExportSetting}
                                    size="xl"
                                    title={viewModel.editingExportSettingIndex >= 0 ? TextConstants.Buttons.Edit : TextConstants.Buttons.Add}
                                    onClose={() => viewModel.resetExportSettingEditorState()}
                                    buttons={[
                                        {
                                            text: TextConstants.Buttons.Confirm,
                                            variant: 'primary',
                                            onClick: () => viewModel.confirmEditExportSetting(),
                                        },
                                        {
                                            text: TextConstants.Buttons.Cancel,
                                            variant: 'secondary',
                                            onClick: () => viewModel.resetExportSettingEditorState(),
                                        },
                                    ]}>
                                    {() => (
                                        <Neo.GridLayout lg={3}>
                                            <Neo.FormGroup label="Days of the Week">
                                                <Neo.AutoCompleteDropDown
                                                    select={viewModel.getDaysOfWeekEnumList}
                                                    bindItems={viewModel.currentExportSetting.meta.selectedDaysOfWeek}
                                                    bindIds={viewModel.currentExportSetting.meta.daysOfWeekNumbers}
                                                    placeholder={TextConstants.PlaceHolder.SelectImportDaysForBroker}
                                                    items={viewModel.getDaysOfWeekEnumList()}
                                                />
                                            </Neo.FormGroup>
                                            <Neo.FormGroup
                                                label={TextConstants.Labels.EventTime}
                                                bind={viewModel.currentExportSetting.meta.eventTime}
                                                dateProps={{ formatString: "HH:mm", timeOnly: true }}
                                            />
                                            <Neo.FormGroup label={TextConstants.Labels.EventType}>
                                                <Neo.DropDown
                                                    select={{
                                                        items: EnumHelper.asList(ExportEventTypeEnum),
                                                        allowNulls: false,
                                                    }}
                                                    bind={viewModel.currentExportSetting.meta.eventType}
                                                />
                                            </Neo.FormGroup>
                                        </Neo.GridLayout>
                                    )}
                                </Neo.Modal>

                                <NeoGrid.Grid<ServiceProviderFileGroupingExportSettingLookup> items={viewModel.exportSettingsList} className="table-striped">
                                    {(item) => (
                                        <NeoGrid.Row className={viewModel.isEditingExportSettingInd && viewModel.editingExportSettingIndex === viewModel.exportSettingsList.indexOf(item) ? "table-primary" : ""}>
                                            <NeoGrid.Column label="Days of the Week">
                                                {viewModel.getExportDaysDisplay(item)}
                                            </NeoGrid.Column>
                                            <NeoGrid.Column label={TextConstants.Labels.EventTime}>
                                                {viewModel.getEventTimeDisplay(item)}
                                            </NeoGrid.Column>
                                            <NeoGrid.Column label={TextConstants.Labels.EventType}>
                                                {viewModel.getEventTypeDisplay(item)}
                                            </NeoGrid.Column>
                                            <NeoGrid.ButtonColumn label="Actions" className="text-center">
                                                <Neo.Button
                                                    onClick={() => viewModel.editExportSetting(item)}
                                                    variant="primary"
                                                    isOutline
                                                    size="sm"
                                                    className="me-1">
                                                    {TextConstants.Buttons.Edit}
                                                </Neo.Button>
                                                <Neo.Button
                                                    onClick={() => viewModel.removeExportSetting(item)}
                                                    variant="danger"
                                                    isOutline
                                                    size="sm">
                                                    {TextConstants.Buttons.Remove}
                                                </Neo.Button>
                                            </NeoGrid.ButtonColumn>
                                        </NeoGrid.Row>
                                    )}
                                </NeoGrid.Grid>
                            </Neo.Card>
                        }


                        <Neo.Card title={this.renderCardTitleWithUnsavedBadge("SFTP Settings", viewModel.hasUnsavedSFTPSettingsChanges)} className="mb-2">
                            {viewModel.managedServiceProviderFileGrouping.staticServiceProviderFileGroupingId > 0 && !viewModel.isEditingSFTPSettingInd &&
                                <div className="d-flex justify-content-end mb-2">
                                    <Neo.Button
                                        onClick={() => viewModel.addNewSFTPDetails(viewModel.managedServiceProviderFileGrouping.staticServiceProviderFileGroupingId)}
                                        variant="primary"
                                        size="sm">
                                        {TextConstants.Buttons.Add}
                                    </Neo.Button>
                                </div>
                            }

                            <Neo.Modal
                                bind={viewModel.meta.isEditingSFTPSettingInd}
                                bindModel={viewModel.meta.currentSFTPSetting}
                                size="xl"
                                title={viewModel.editingSFTPSettingIndex >= 0 ? TextConstants.Buttons.Edit : TextConstants.Buttons.Add}
                                onClose={() => viewModel.resetSFTPEditorState()}
                                buttons={[
                                    {
                                        text: TextConstants.Buttons.Confirm,
                                        variant: 'primary',
                                        onClick: () => viewModel.confirmEditSFTPSetting(),
                                    },
                                    {
                                        text: TextConstants.Buttons.Cancel,
                                        variant: 'secondary',
                                        onClick: () => viewModel.resetSFTPEditorState(),
                                    },
                                ]}>
                                {() => (
                                    <React.Fragment>
                                        <Neo.GridLayout lg={3}>
                                            {viewModel.editingSFTPSettingIndex >= 0
                                                ? <Neo.FormGroupInline label={TextConstants.Labels.SFTPSetting} display={viewModel.currentSFTPSetting.meta.sftpSetting} />
                                                : <Neo.FormGroupInline
                                                    label={TextConstants.Labels.SFTPSetting}
                                                    bind={viewModel.currentSFTPSetting.meta.sftpDetailId}
                                                    select={{ items: viewModel.sftpDetailsList, displayMember: 'sftpSetting', valueMember: 'sftpdetailId' }}
                                                    onChange={() => viewModel.setSelectedSFTPDetail()}
                                                />
                                            }
                                            <Neo.FormGroupInline label={TextConstants.Labels.SFTPDirectory} bind={viewModel.currentSFTPSetting.meta.sftpDirectory} />
                                            <Neo.FormGroupInline label={TextConstants.Labels.PrimeTimeCronSchedule} bind={viewModel.currentSFTPSetting.meta.primeTimeCron} />
                                        </Neo.GridLayout>
                                        <Neo.GridLayout lg={3}>
                                            <Neo.FormGroupInline label={TextConstants.Labels.LateCronSchedule} bind={viewModel.currentSFTPSetting.meta.lateCron} />
                                            <Neo.FormGroupInline label="Trade SFTP Type">
                                                <Neo.DropDown
                                                    select={{
                                                        items: this.props.tradeSftpTypeOptions,
                                                        allowNulls: true,
                                                    }}
                                                    bind={viewModel.currentSFTPSetting.meta.tradeSftpTypeId}
                                                    placeholder="Select Trade SFTP Type"
                                                />
                                            </Neo.FormGroupInline>
                                        </Neo.GridLayout>
                                        <Neo.GridLayout lg={3}>
                                            <Neo.FormGroupInline label={TextConstants.Labels.ExportLocation} bind={viewModel.currentSFTPSetting.meta.isExportInd} />
                                            <Neo.FormGroupInline label={TextConstants.Labels.IsActive} bind={viewModel.currentSFTPSetting.meta.isActiveInd} />
                                            <Neo.FormGroupInline
                                                label={TextConstants.Labels.IsTradeOrder}
                                                bind={viewModel.currentSFTPSetting.meta.isTradeOrder}
                                                type="checkbox"
                                            />
                                            <Neo.FormGroupInline
                                                label={TextConstants.Labels.IsValuation}
                                                bind={viewModel.currentSFTPSetting.meta.isValuation}
                                                type="checkbox"
                                            />
                                        </Neo.GridLayout>
                                    </React.Fragment>
                                )}
                            </Neo.Modal>

                            <NeoGrid.Grid items={viewModel.sftpBrokerDetailsList}>
                                {(item, meta) => (
                                    <NeoGrid.Row className={viewModel.isEditingSFTPSettingInd && viewModel.editingSFTPSettingIndex === viewModel.sftpBrokerDetailsList.indexOf(item) ? 'table-primary' : ''}>
                                        <NeoGrid.Column display={meta.sftpSetting} label={TextConstants.Labels.SFTPSetting} />
                                        <NeoGrid.Column display={meta.sftpDirectory} label={TextConstants.Labels.SFTPDirectory} />
                                        <NeoGrid.Column display={meta.isExportInd} label={TextConstants.Labels.ExportLocation} />
                                        <NeoGrid.Column display={meta.primeTimeCron} label={TextConstants.Labels.PrimeTimeCronSchedule} />
                                        <NeoGrid.Column display={meta.lateCron} label={TextConstants.Labels.LateCronSchedule} />
                                        <NeoGrid.Column display={meta.isActiveInd} label={TextConstants.Labels.IsActive} />
                                        <NeoGrid.Column display={meta.isTradeOrder} label={TextConstants.Labels.IsTradeOrder} />
                                        <NeoGrid.Column display={meta.isValuation} label={TextConstants.Labels.IsValuation} />
                                        <NeoGrid.ButtonColumn label="Actions" className="text-center">
                                            <Neo.Button
                                                onClick={() => viewModel.editSFTPDetails(item)}
                                                variant="primary"
                                                isOutline
                                                size="sm"
                                                className="me-1">
                                                {TextConstants.Buttons.Edit}
                                            </Neo.Button>
                                            <Neo.Button
                                                onClick={() => viewModel.removeSFTPSetting(item)}
                                                variant="danger"
                                                isOutline
                                                size="sm">
                                                {TextConstants.Buttons.Remove}
                                            </Neo.Button>
                                        </NeoGrid.ButtonColumn>
                                    </NeoGrid.Row>
                                )}
                            </NeoGrid.Grid>
                        </Neo.Card>
                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <Neo.Button variant="primary" isSubmit>
                                {TextConstants.Buttons.Save}
                            </Neo.Button>
                            <Neo.Button
                                onClick={() => this.handlePageCancel()}
                                variant="secondary">
                                {TextConstants.Buttons.Cancel}
                            </Neo.Button>
                        </div>
                    </Neo.Card>
                </Neo.Form>
            </React.Fragment>
        );
    }

    private onFormInvalid(e: React.FormEvent, justValidated?: boolean) {
        if (!justValidated) {
            ModalUtils.showMessage("Save", TextConstants.Validation.InvalidServiceProviderDetails);
        }
    }
}